import type { Contact } from '@taler/api-client'
import { contactKeys } from '@taler/api-client'
import type { Money } from '@taler/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { isNotFound } from '@/app/not-found'
import { isCapabilityDenied, problemDetail } from '../internal/problem.ts'

export interface UpdateContactInput {
  readonly name?: string
  readonly notes?: string
}

export interface UseContactOptions {
  readonly ledgerId: () => string | undefined
  readonly contactId: () => string
  readonly enabled: () => boolean
}

export interface UseContact {
  readonly contact: ComputedRef<Contact | undefined>
  readonly balances: ComputedRef<readonly Money[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly notFound: ComputedRef<boolean>
  readonly retrying: ComputedRef<boolean>
  readonly isDeleting: ComputedRef<boolean>
  readonly hasDeleteError: ComputedRef<boolean>
  /** The server's own reason, when it sent one - undefined for a network/unknown failure. */
  readonly deleteErrorDetail: ComputedRef<string | undefined>
  readonly deleteDenied: ComputedRef<boolean>
  readonly isUpdating: ComputedRef<boolean>
  readonly hasUpdateError: ComputedRef<boolean>
  readonly updateErrorDetail: ComputedRef<string | undefined>
  deleteContact(): Promise<void>
  resetDelete(): void
  updateContact(input: UpdateContactInput): Promise<Contact>
  retry(): void
}

export function useContact(options: UseContactOptions): UseContact {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const contactQuery = useQuery({
    queryKey: computed(() => contactKeys.detail(options.ledgerId() ?? '', options.contactId())),
    queryFn: ({ signal }) => api.contacts.get(options.ledgerId()!, options.contactId(), signal),
    enabled: active,
    retry: false,
  })

  const balancesQuery = useQuery({
    queryKey: computed(() => contactKeys.balances(options.ledgerId() ?? '', options.contactId())),
    queryFn: ({ signal }) => api.contacts.balances(options.ledgerId()!, options.contactId(), signal),
    enabled: active,
    retry: false,
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.contacts.delete(options.ledgerId()!, options.contactId()),
    onSuccess: () => {
      for (const key of contactKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  const updateMutation = useMutation({
    mutationFn: (input: UpdateContactInput) =>
      api.contacts.update(options.ledgerId()!, options.contactId(), input),
    onSuccess: () => {
      for (const key of contactKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  return {
    contact: computed(() => contactQuery.data.value),
    balances: computed(() => balancesQuery.data.value ?? []),
    isLoading: computed(
      () => active.value && (contactQuery.isPending.value || balancesQuery.isPending.value),
    ),
    error: computed(() => contactQuery.error.value ?? balancesQuery.error.value),
    notFound: computed(() => isNotFound(contactQuery.error.value)),
    retrying: retrying(contactQuery, balancesQuery),
    isDeleting: computed(() => deleteMutation.isPending.value),
    hasDeleteError: computed(() => deleteMutation.error.value !== null),
    deleteErrorDetail: computed(() => problemDetail(deleteMutation.error.value)),
    deleteDenied: computed(() => isCapabilityDenied(deleteMutation.error.value)),
    isUpdating: computed(() => updateMutation.isPending.value),
    hasUpdateError: computed(() => updateMutation.error.value !== null),
    updateErrorDetail: computed(() => problemDetail(updateMutation.error.value)),
    deleteContact: () => deleteMutation.mutateAsync(),
    resetDelete: () => deleteMutation.reset(),
    updateContact: (input) => updateMutation.mutateAsync(input),
    retry: () => {
      void contactQuery.refetch()
      void balancesQuery.refetch()
    },
  }
}
